import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { localizeError } from "@/lib/errorMessages";
import { useMediaQuery } from "@/features/lead/hooks/use-media-query";
import { CustomerForm } from "@/features/customer/components/customer-form";
import { useCreateCustomer } from "@/features/customer/api";
import type { Customer, CustomerCreateRequest } from "@/types/customer";

interface CustomerCreateSheetProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful create. Receives the new customer so callers
   *  can navigate or refresh. */
  onCreated?: (customer: Customer) => void;
}

export function CustomerCreateSheet({
  open,
  onClose,
  onCreated,
}: CustomerCreateSheetProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const mutation = useCreateCustomer();

  const handleSubmit = async (
    payload: CustomerCreateRequest | Partial<CustomerCreateRequest>,
  ): Promise<Customer> => {
    // Create always sends a full payload; the type widens to satisfy the
    // shared form interface. Errors propagate up so the form can map 409
    // CUSTOMER_PHONE_DUPLICATE → inline phone-field error; the toast at the
    // outer onSubmit picks up anything else.
    const customer = await mutation.mutateAsync(payload as CustomerCreateRequest);
    toast.success("Đã thêm khách hàng");
    onCreated?.(customer);
    onClose();
    return customer;
  };

  // Anything that escapes the form's local handler ends up here.
  const onCaughtError = (error: unknown) => {
    toast.error(localizeError(error));
  };

  const body = (
    <CustomerForm
      mode="create"
      onSubmit={async (payload) => {
        try {
          return await handleSubmit(payload);
        } catch (error) {
          // Surface unknown errors as a toast, then re-throw so RHF stops
          // its submit cycle (which keeps the submit button disabled until
          // the user changes something).
          onCaughtError(error);
          throw error;
        }
      }}
      onCancel={onClose}
      submitLabel="Tạo khách hàng"
    />
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Thêm khách hàng</DialogTitle>
            <DialogDescription>
              Nhập thông tin khách hàng để bắt đầu theo dõi.
            </DialogDescription>
          </DialogHeader>
          {body}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Thêm khách hàng</SheetTitle>
          <SheetDescription>
            Nhập thông tin khách hàng để bắt đầu theo dõi.
          </SheetDescription>
        </SheetHeader>
        <div className="px-4 pb-6">{body}</div>
      </SheetContent>
    </Sheet>
  );
}
